import { ticks } from './config';
import { PowerupKind } from './economy';

/**
 * Jungle creeps (ADR-0019): small neutral monsters living in camps in the jungle, worth a little gold. They mind
 * their own business until someone hurts one — then the whole camp turns on that player, chases them within
 * `CREEP.leashRadius` of the camp and gives up, walks home and heals when led further. A cleared camp grows back
 * `CREEP.respawnTicks` later. No announcements: they are farm, not events. Each camp has one prize, the same every time
 * for its creature (ADR-0067): the classic four leave their power-up on the altar (`drop`); the Hedgehog (ADR-0053)
 * curls into a ball that sends bullets back as spines (`HEDGEHOG`), its fight its only prize; Magpies steal gold into
 * their nest's hoard, which the last hit takes (`MAGPIE`); Lantern Moths shield their Lantern, which plants a vision
 * ward for whoever bursts it (`MOTH`, `WARD`).
 */
export const CreepKind = {
  Snail: 0,
  Wasp: 1,
  Toad: 2,
  Shroom: 3,
  Hedgehog: 4,
  Magpie: 5,
  Moth: 6,
  /** The Lantern a Moth camp guards (not a camp of its own: `CreepDef.camp`). */
  Lantern: 7,
} as const;
export type CreepKind = (typeof CreepKind)[keyof typeof CreepKind];

export interface CreepDef {
  readonly kind: CreepKind;
  readonly key: 'snail' | 'wasp' | 'toad' | 'shroom' | 'hedgehog' | 'magpie' | 'moth' | 'lantern';
  readonly name: string;
  readonly maxHp: number;
  readonly bodyRadius: number;
  readonly hurtRadius: number;
  /** px/s; 0 = stays put (it shoots instead). */
  readonly speed: number;
  /** Per attack: a bite within `reach` px (edge to edge) or, `ranged`, a spore at up to `reach` px. */
  readonly damage: number;
  readonly reach: number;
  readonly attackTicks: number;
  readonly ranged: boolean;
  /** Gold for the player who dealt it the most damage. */
  readonly gold: number;
  /** How many live in one camp. */
  readonly count: number;
  /**
   * The power-up (content PowerupKind) a camp of these leaves on its altar once cleared (ADR-0067), -1 for none: the
   * special camps' prize is their own (the tame, the hoard, the ward).
   */
  readonly drop: number;
  /** Can a camp be of this creature? (The Lantern only lives in a Moth camp.) */
  readonly camp: boolean;
  /** Minimap / UI colour. */
  readonly color: number;
}

export const CREEPS: readonly CreepDef[] = [
  {
    kind: CreepKind.Snail,
    key: 'snail',
    name: 'Garden Snail',
    maxHp: 180,
    bodyRadius: 7,
    hurtRadius: 8,
    speed: 20,
    damage: 14,
    reach: 6,
    attackTicks: ticks(1.3),
    ranged: false,
    gold: 32,
    count: 1,
    drop: PowerupKind.Speed,
    camp: true,
    color: 0xb86f50,
  },
  {
    kind: CreepKind.Wasp,
    key: 'wasp',
    name: 'Wasp',
    maxHp: 45,
    bodyRadius: 4,
    hurtRadius: 6,
    speed: 72,
    damage: 5,
    reach: 5,
    attackTicks: ticks(0.6),
    ranged: false,
    gold: 12,
    count: 3,
    drop: PowerupKind.Rage,
    camp: true,
    color: 0xfee761,
  },
  {
    kind: CreepKind.Toad,
    key: 'toad',
    name: 'Toad',
    maxHp: 110,
    bodyRadius: 6,
    hurtRadius: 7,
    speed: 44,
    damage: 12,
    reach: 20,
    attackTicks: ticks(1.1),
    ranged: false,
    gold: 22,
    count: 2,
    drop: PowerupKind.Heal,
    camp: true,
    color: 0x63c74d,
  },
  {
    kind: CreepKind.Shroom,
    key: 'shroom',
    name: 'Spore Shroom',
    maxHp: 120,
    bodyRadius: 6,
    hurtRadius: 7,
    speed: 0,
    damage: 10,
    reach: 150,
    attackTicks: ticks(1.4),
    ranged: true,
    gold: 28,
    count: 1,
    drop: PowerupKind.Shield,
    camp: true,
    color: 0xb55088,
  },
  {
    kind: CreepKind.Hedgehog,
    key: 'hedgehog',
    name: 'Hedgehog',
    maxHp: 180,
    bodyRadius: 6,
    hurtRadius: 7,
    speed: 36,
    damage: 12,
    reach: 6,
    attackTicks: ticks(1.2),
    ranged: false,
    // No gold, no power-up: its fight is the prize (its Hedgeball went with Classic, ADR-0209).
    gold: 0,
    count: 1,
    drop: -1,
    camp: true,
    color: 0xe8b796,
  },
  {
    kind: CreepKind.Magpie,
    key: 'magpie',
    name: 'Magpie',
    maxHp: 38,
    bodyRadius: 4,
    hurtRadius: 6,
    speed: 105,
    damage: 4,
    reach: 5,
    attackTicks: ticks(0.4),
    ranged: false,
    // A little each; the nest's hoard is the prize (MAGPIE).
    gold: 4,
    count: 3,
    drop: -1,
    camp: true,
    color: 0xc0cbdc,
  },
  {
    kind: CreepKind.Moth,
    key: 'moth',
    name: 'Lantern Moth',
    maxHp: 26,
    bodyRadius: 4,
    hurtRadius: 6,
    speed: 85,
    damage: 4,
    reach: 5,
    attackTicks: ticks(0.8),
    ranged: false,
    gold: 4,
    // The camp: a Lantern (grown with them) and this many moths circling it (MOTH).
    count: 3,
    drop: -1,
    camp: true,
    color: 0x2ce8f5,
  },
  {
    kind: CreepKind.Lantern,
    key: 'lantern',
    name: 'Lantern',
    maxHp: 140,
    bodyRadius: 6,
    hurtRadius: 8,
    speed: 0,
    // Harmless: its moths do the fighting, and while one lives nothing hurts it (MOTH).
    damage: 0,
    reach: 0,
    attackTicks: ticks(1),
    ranged: false,
    gold: 10,
    count: 1,
    drop: -1,
    camp: false,
    color: 0x2ce8f5,
  },
];

/** The creatures a camp can be (every CreepKind but the Lantern). */
export const CAMP_KINDS: readonly CreepKind[] = CREEPS.filter((c) => c.camp).map((c) => c.kind);

/**
 * Magpies (ADR-0067): a flock around a nest (the altar). They mob whoever hurts one or walks up to the nest: a magpie
 * swoops in, pecks (its `damage`) and steals `steal` gold from that player into the nest's hoard, then wheels away for
 * `wheelTicks` before the next swoop. The hoard starts at `hoard` when the camp grows; the last hit on the last magpie
 * takes all of it (at most `maxHoard`).
 */
export const MAGPIE = {
  hoard: 50,
  steal: 6,
  maxHoard: 400,
  /** A player this close to the nest is mobbed even without shooting (px). */
  noticeRadius: 60,
  wheelTicks: ticks(1),
} as const;

/**
 * Lantern Moths (ADR-0067): `CreepDef.count` moths circle their Lantern `orbit` px out at `orbitSpeed` radians a second.
 * While one of them lives, nothing hurts the Lantern (hits are blocked). They dart at an attacker within `dart` px of
 * the Lantern and bite, then flutter back into orbit; a dead moth grows back `regrowTicks` later while the Lantern still
 * glows. Burst the Lantern in the gap: it plants a vision ward (`WARD`) for the team of whoever did.
 */
export const MOTH = {
  orbit: 18,
  orbitSpeed: 2.4,
  dart: 80,
  regrowTicks: ticks(5),
} as const;

/**
 * A vision ward (ADR-0067), planted where a Lantern burst: for `ticks` its team sees the jungle within `radius` px of it
 * (walls still cast their shadows, like a player's sight all round). Enemies can shoot it down (`hp`).
 */
export const WARD = {
  hp: 60,
  hurtRadius: 6,
  radius: 200,
  ticks: ticks(120),
} as const;

/**
 * The Hedgehog (ADR-0053). Burst it and it curls up: `curlDamage` taken within `curlWindowTicks` rolls it into a ball
 * for `curlTicks`, during which nothing hurts it and every bullet that meets it comes straight back as a SPINE (one per
 * `spineEveryTicks` at most — a Minigun gets a few, not thirty; 5 a second since ADR-0080, was 10). Unrolled, it can't curl again for `guardTicks`:
 * the window to pour damage in. So: burst, hold fire, burst.
 */
export const HEDGEHOG = {
  curlDamage: 40,
  curlWindowTicks: ticks(1),
  curlTicks: ticks(1),
  guardTicks: ticks(2),
  spineEveryTicks: 6,
} as const;

export const CREEP = {
  /** They chase an attacker at most this far from their camp's centre, then walk home. */
  leashRadius: 120,
  /** An attacker further than this from the camp is let go. */
  forgetRadius: 180,
  /** Healing per tick while walking home or resting (fraction of max). */
  regenPerTick: 0.02,
  /** Walking home longer than this: they are back (slipped round whatever blocked them). */
  returnTicks: ticks(5),
  /** Camps appear this long into a round, and grow back this long after being cleared. */
  firstTicks: ticks(10),
  respawnTicks: ticks(40),
  /** How the creeps of one camp stand around its centre, px. */
  spread: 9,
} as const;

export function creepDef(kind: number): CreepDef {
  return CREEPS[kind] ?? (CREEPS[0] as CreepDef);
}
